import React, { useEffect, useState } from "react";
import API_BASE from "../config/api";
import SetupGuideModal from "./SetupGuideModal";

/*
  Reusable field for any image-URL input (logo, cover, gallery, product images).
  Preloads the URL in the browser and shows a live thumbnail once it resolves,
  or a warning if it doesn't — this is where mistakes like pasting a Pixabay/
  Unsplash PAGE link (e.g. pixabay.com/photos/xxxx) instead of the direct image
  file get caught, before the customer's site ever goes live with a broken photo.
*/
function ImageUrlField({ value, onChange, placeholder, hint = true }) {
    const [status, setStatus] = useState("idle"); // idle | checking | ok | error

    useEffect(() => {
        if (!value || !value.trim()) { setStatus("idle"); return; }
        let cancelled = false;
        setStatus("checking");
        const img = new Image();
        img.onload = () => { if (!cancelled) setStatus("ok"); };
        img.onerror = () => { if (!cancelled) setStatus("error"); };
        img.src = value;
        return () => { cancelled = true; };
    }, [value]);

    return (
        <div className="dn-imgfield">
            <div className="dn-imgfield-row">
                <input
                    placeholder={placeholder || "https://example.com/photo.jpg"}
                    className="form-control form-control-sm"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                />
                {status !== "idle" && (
                    <div className={`dn-imgfield-preview ${status}`} title={status === "error" ? "Couldn't load this as an image" : status === "ok" ? "Looks good" : "Checking…"}>
                        {status === "ok" && <img src={value} alt="" />}
                        {status === "error" && <i className="bi bi-exclamation-triangle-fill" />}
                        {status === "checking" && <i className="bi bi-hourglass-split" />}
                    </div>
                )}
            </div>
            {status === "error" && (
                <div className="dn-imgfield-warn">
                    <i className="bi bi-exclamation-circle me-1" />
                    This doesn't look like a direct image link — it may be a webpage (e.g. a Pixabay or Unsplash photo page) rather than the image file itself. Your site will show a placeholder instead of this photo.
                </div>
            )}
            {hint && status === "idle" && (
                <div className="dn-imgfield-hint">
                    Tip: open the photo full-size, right-click it and choose <b>"Copy image address"</b> — the link should end in .jpg, .png or .webp. A page link like pixabay.com/photos/... won't work.
                </div>
            )}
        </div>
    );
}

/*
  Dashboard page to manage the customer's Business Website (IndiaMART style).
  props: userData, listinguid
*/
export default function BusinessWebsiteManager({ userData, listinguid ,  onBack}) {
    const localUser = JSON.parse(localStorage.getItem("user") || "{}");
    const personaluid = userData?.personaluid || localUser?.personaluid;

    const [form, setForm] = useState({
        name: "", tagline: "", logo: "", cover: "", phone: "", email: "",
        address: "", website: "", about: "",
        linkfacebook: "", linkinstagram: "", linklinkedin: "",
        nature_of_business: "", year_established: "", gst_no: "", legal_status: "", employees: "", annual_turnover: "",
    });
    const [products, setProducts] = useState([{ category: "", name: "", image: "", price: "", unit: "", description: "" }]);
    const [hours, setHours] = useState([{ day: "", time: "" }]);
    const [gallery, setGallery] = useState([""]);
    const [domain, setDomain] = useState("");
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);

    useEffect(() => {
        if (!personaluid) return;
        fetch(`${API_BASE}/api/get_business_site.php?personaluid=${encodeURIComponent(personaluid)}`)
            .then((r) => r.json())
            .then((res) => {
                if (res.domain) setDomain(res.domain);
                if (res.success && res.data) {
                    const d = res.data;
                    setForm({
                        name: d.name || "", tagline: d.tagline || "", logo: d.logo || "", cover: d.cover || "",
                        phone: d.phone || "", email: d.email || "", address: d.address || "", website: d.website || "",
                        about: d.about || "", linkfacebook: d.linkfacebook || "", linkinstagram: d.linkinstagram || "",
                        linklinkedin: d.linklinkedin || "",
                        nature_of_business: d.nature_of_business || "", year_established: d.year_established || "",
                        gst_no: d.gst_no || "", legal_status: d.legal_status || "", employees: d.employees || "",
                        annual_turnover: d.annual_turnover || "",
                    });
                    setProducts(d.products?.length ? d.products : [{ category: "", name: "", image: "", price: "", unit: "", description: "" }]);
                    setHours(d.hours?.length ? d.hours : [{ day: "", time: "" }]);
                    setGallery(d.gallery?.length ? d.gallery : [""]);
                }
            })
            .catch(() => { });
    }, [personaluid]);

    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
    const updList = (list, setList, i, key, val) => { const c = [...list]; c[i] = { ...c[i], [key]: val }; setList(c); };

    const handleSave = async () => {
        if (!form.name.trim()) { setMsg({ type: "warning", text: "Business name is required." }); return; }
        setSaving(true); setMsg(null);
        try {
            const res = await fetch(`${API_BASE}/api/save_business_site.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    personaluid, listinguid, ...form,
                    products: products.filter((p) => (p.name || "").trim() !== ""),
                    hours: hours.filter((h) => (h.day || "").trim() !== ""),
                    gallery: gallery.filter((g) => (g || "").trim() !== ""),
                }),
            });
            const data = await res.json();
            setSaving(false);
            if (data.success) { setDomain(data.domain); setMsg({ type: "success", text: "Business website saved successfully." }); }
            else setMsg({ type: "danger", text: data.message || "Could not save." });
        } catch { setSaving(false); setMsg({ type: "danger", text: "Network error while saving." }); }
    };

    return (
        <div className="container-fluid py-2" style={{ maxWidth: 980 }}>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2"> 
                <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                        onClick={() => onBack && onBack()}
                    >
                        <i className="bi bi-arrow-left"></i> Back
                    </button>
                <h5 className="fw-bold mb-0">Business Website</h5>
                <div className="d-flex align-items-center gap-2">
                    <SetupGuideModal
                        storageKey={`dn_guide_business_${personaluid || "me"}`}
                        title="Set up your Business Website"
                        intro="Welcome! Fill these sections to launch your business website. It only takes a few minutes."
                        steps={[
                            { title: "Business Details", desc: "Add business name, tagline, logo, cover image, contact info and an About description." },
                            { title: "Company Factsheet", desc: "Nature of business, year established, employees, turnover and GST." },
                            { title: "Products & Services", desc: "Click '+ Add Product' to add items with category, price, unit and image." },
                            { title: "Gallery & Hours", desc: "Add photo URLs and your operating hours." },
                            { title: "Social Links", desc: "Add Facebook, Instagram and LinkedIn, then click Save Website." },
                        ]}
                    />
                    {domain && (
                        <a className="btn btn-sm btn-outline-success" target="_blank" rel="noreferrer" href={`/business-website/${domain}`}>
                            <i className="bi bi-box-arrow-up-right me-1" /> View Live Website
                        </a>
                    )}
                </div>
            </div>

            {msg && <div className={`alert alert-${msg.type} py-2`}>{msg.text}</div>}

            {/* BASIC */}
            <div className="card shadow-sm mb-3">
                <div className="card-header bg-light fw-semibold">Business Details</div>
                <div className="card-body"><div className="row g-2">
                    <div className="col-md-6"><label className="form-label small">Business Name *</label><input className="form-control form-control-sm" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
                    <div className="col-md-6"><label className="form-label small">Tagline</label><input className="form-control form-control-sm" value={form.tagline} onChange={(e) => set("tagline", e.target.value)} /></div>
                    <div className="col-md-6"><label className="form-label small">Phone</label><input className="form-control form-control-sm" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
                    <div className="col-md-6"><label className="form-label small">Email</label><input className="form-control form-control-sm" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
                    <div className="col-md-6"><label className="form-label small">Address</label><input className="form-control form-control-sm" value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
                    <div className="col-md-6"><label className="form-label small">Website URL</label><input className="form-control form-control-sm" value={form.website} onChange={(e) => set("website", e.target.value)} /></div>
                    <div className="col-md-6">
                        <label className="form-label small">Logo image URL</label>
                        <ImageUrlField value={form.logo} onChange={(v) => set("logo", v)} placeholder="https://your-logo.png" />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label small">Homepage Background / Cover Image URL</label>
                        <ImageUrlField value={form.cover} onChange={(v) => set("cover", v)} placeholder="https://your-image.jpg (shown behind the hero)" />
                    </div>
                    <div className="col-12"><label className="form-label small">About / Description</label><textarea rows="3" className="form-control form-control-sm" value={form.about} onChange={(e) => set("about", e.target.value)} /></div>
                </div></div>
            </div>

            {/* COMPANY FACTSHEET */}
            <div className="card shadow-sm mb-3">
                <div className="card-header bg-light fw-semibold">Company Factsheet</div>
                <div className="card-body"><div className="row g-2">
                    <div className="col-md-6"><label className="form-label small">Nature of Business</label><input className="form-control form-control-sm" placeholder="Retailer / Wholesaler / Manufacturer" value={form.nature_of_business} onChange={(e) => set("nature_of_business", e.target.value)} /></div>
                    <div className="col-md-6"><label className="form-label small">Year of Establishment</label><input className="form-control form-control-sm" value={form.year_established} onChange={(e) => set("year_established", e.target.value)} /></div>
                    <div className="col-md-6"><label className="form-label small">Legal Status of Firm</label><input className="form-control form-control-sm" placeholder="Proprietorship / Pvt Ltd" value={form.legal_status} onChange={(e) => set("legal_status", e.target.value)} /></div>
                    <div className="col-md-6"><label className="form-label small">No. of Employees</label><input className="form-control form-control-sm" value={form.employees} onChange={(e) => set("employees", e.target.value)} /></div>
                    <div className="col-md-6"><label className="form-label small">Annual Turnover</label><input className="form-control form-control-sm" value={form.annual_turnover} onChange={(e) => set("annual_turnover", e.target.value)} /></div>
                    <div className="col-md-6"><label className="form-label small">GST No.</label><input className="form-control form-control-sm" value={form.gst_no} onChange={(e) => set("gst_no", e.target.value)} /></div>
                </div></div>
            </div>

            {/* PRODUCTS */}
            <div className="card shadow-sm mb-3">
                <div className="card-header bg-light fw-semibold d-flex justify-content-between align-items-center">
                    <span>Products &amp; Services</span>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setProducts([...products, { category: "", name: "", image: "", price: "", unit: "", description: "" }])}>+ Add Product</button>
                </div>
                <div className="card-body">
                    {products.map((p, i) => (
                        <div className="border rounded p-2 mb-2" key={i}>
                            <div className="row g-2">
                                <div className="col-md-4"><input placeholder="Product name *" className="form-control form-control-sm" value={p.name} onChange={(e) => updList(products, setProducts, i, "name", e.target.value)} /></div>
                                <div className="col-md-4"><input placeholder="Category" className="form-control form-control-sm" value={p.category} onChange={(e) => updList(products, setProducts, i, "category", e.target.value)} /></div>
                                <div className="col-md-3"><input placeholder="Price (blank = Ask Price)" className="form-control form-control-sm" value={p.price} onChange={(e) => updList(products, setProducts, i, "price", e.target.value)} /></div>
                                <div className="col-md-1"><button type="button" className="btn btn-sm btn-outline-danger w-100" onClick={() => setProducts(products.filter((_, x) => x !== i))}>×</button></div>
                                <div className="col-md-3"><input placeholder="Unit (piece/kg)" className="form-control form-control-sm" value={p.unit} onChange={(e) => updList(products, setProducts, i, "unit", e.target.value)} /></div>
                                <div className="col-md-9">
                                    <ImageUrlField value={p.image} onChange={(v) => updList(products, setProducts, i, "image", v)} placeholder="Product image URL" hint={false} />
                                </div>
                                <div className="col-12"><input placeholder="Short description" className="form-control form-control-sm" value={p.description} onChange={(e) => updList(products, setProducts, i, "description", e.target.value)} /></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* GALLERY */}
            <div className="card shadow-sm mb-3">
                <div className="card-header bg-light fw-semibold d-flex justify-content-between align-items-center">
                    <span>Gallery image URLs</span>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setGallery([...gallery, ""])}>+ Add</button>
                </div>
                <div className="card-body">
                    {gallery.map((g, i) => (
                        <div className="row g-2 mb-2 align-items-start" key={i}>
                            <div className="col-md-11">
                                <ImageUrlField value={g} onChange={(v) => { const c = [...gallery]; c[i] = v; setGallery(c); }} placeholder="https://image-url.jpg" hint={i === 0} />
                            </div>
                            <div className="col-md-1"><button type="button" className="btn btn-sm btn-outline-danger w-100" onClick={() => setGallery(gallery.filter((_, x) => x !== i))}>×</button></div>
                        </div>
                    ))}
                </div>
            </div>

            {/* HOURS */}
            <div className="card shadow-sm mb-3">
                <div className="card-header bg-light fw-semibold d-flex justify-content-between align-items-center">
                    <span>Operating Hours</span>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setHours([...hours, { day: "", time: "" }])}>+ Add</button>
                </div>
                <div className="card-body">
                    {hours.map((h, i) => (
                        <div className="row g-2 mb-2" key={i}>
                            <div className="col-md-5"><input placeholder="Day (e.g. Sun - Fri)" className="form-control form-control-sm" value={h.day} onChange={(e) => updList(hours, setHours, i, "day", e.target.value)} /></div>
                            <div className="col-md-6"><input placeholder="Time (e.g. 9AM - 6PM or Closed)" className="form-control form-control-sm" value={h.time} onChange={(e) => updList(hours, setHours, i, "time", e.target.value)} /></div>
                            <div className="col-md-1"><button type="button" className="btn btn-sm btn-outline-danger w-100" onClick={() => setHours(hours.filter((_, x) => x !== i))}>×</button></div>
                        </div>
                    ))}
                </div>
            </div>

            {/* SOCIAL */}
            <div className="card shadow-sm mb-3">
                <div className="card-header bg-light fw-semibold">Social Links</div>
                <div className="card-body"><div className="row g-2">
                    <div className="col-md-4"><label className="form-label small">Facebook</label><input className="form-control form-control-sm" value={form.linkfacebook} onChange={(e) => set("linkfacebook", e.target.value)} /></div>
                    <div className="col-md-4"><label className="form-label small">Instagram</label><input className="form-control form-control-sm" value={form.linkinstagram} onChange={(e) => set("linkinstagram", e.target.value)} /></div>
                    <div className="col-md-4"><label className="form-label small">LinkedIn</label><input className="form-control form-control-sm" value={form.linklinkedin} onChange={(e) => set("linklinkedin", e.target.value)} /></div>
                </div></div>
            </div>

            <div className="d-flex justify-content-end gap-2 mb-4">
                <button className="btn btn-primary px-4" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save Website"}</button>
            </div>

            <style>{`
                .dn-imgfield-row{display:flex;gap:8px;align-items:center;}
                .dn-imgfield-row .form-control{flex:1;}
                .dn-imgfield-preview{width:32px;height:32px;border-radius:6px;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:14px;overflow:hidden;border:1px solid #dee2e6;}
                .dn-imgfield-preview img{width:100%;height:100%;object-fit:cover;}
                .dn-imgfield-preview.ok{border-color:#a3d9b1;}
                .dn-imgfield-preview.error{background:#fdecec;color:#c0392b;}
                .dn-imgfield-preview.checking{background:#f1f3f5;color:#868e96;}
                .dn-imgfield-warn{font-size:11.5px;color:#c0392b;margin-top:4px;line-height:1.4;}
                .dn-imgfield-hint{font-size:11.5px;color:#868e96;margin-top:4px;line-height:1.4;}
            `}</style>
        </div>
    );
}